import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Link2, Sliders, Rocket } from "lucide-react";

const steps = [
  {
    number: 1,
    icon: Link2,
    title: "Connect",
    text: "Securely link your Gmail, Outlook, Slack, and Teams accounts via our official APIs. No passwords shared.",
  },
  {
    number: 2,
    icon: Sliders,
    title: "Customize",
    text: "Set your AI tone (Professional, Friendly, Casual) and upload your business FAQs.",
  },
  {
    number: 3,
    icon: Rocket,
    title: "Autopilot",
    text: "Watch as your inbox organizes itself. Review drafts, confirm priorities, and reclaim your day.",
  },
];

export const Setup3StepSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section id="setup" className="py-24 relative overflow-hidden">
      <div className="absolute inset-0 noise-texture" />
      <div className="container relative z-10" ref={ref}>
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-lg mb-4">Up and Running in 120 Seconds.</h2>
          <p className="body-lg max-w-xl mx-auto text-muted-foreground">
            This isn&apos;t complex enterprise software. It works now.
          </p>
        </motion.div>

        {/* Three cards with glowing connecting line */}
        <div className="relative grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {/* Glowing line between cards (visible on desktop) */}
          <div
            className="hidden md:block absolute top-[72px] left-[16.666%] right-[16.666%] h-0.5 bg-gradient-to-r from-transparent via-primary/50 to-transparent"
            style={{ boxShadow: "0 0 20px rgba(59, 130, 246, 0.4)" }}
          />

          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={step.number}
                className="relative glass-panel p-8 rounded-2xl border border-white/10 text-center"
                initial={{ opacity: 0, y: 30 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: i * 0.15 }}
              >
                {/* Numbered icon - circle with step number, icon below */}
                <div className="relative z-10 inline-flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 border-2 border-primary/40 text-primary font-bold text-lg mb-4">
                  {step.number}
                </div>
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-white/5 mb-4">
                  <Icon className="w-6 h-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold mb-3">{step.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
